import React, { useState, useContext } from "react";
import theme from "assets/theme";
import { Box, Grid, Card, Stack } from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import SpeedIcon from "@mui/icons-material/Speed";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { CartContext } from "utils/context/cart.context";

// Components
import SchemaComponent from "components/CBSchema";
import SEO from "components/CBSEO";
import CBLoading from "components/CBLoading";
import CBDivider from "components/CBDivider";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import HeaderContainer from "styles/components/headerContainer";
import ListComponent from "components/CBList";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DynamicEHRSection from "./components/DynamicEHRSection";
import CBHowItWorks from "components/CBHowItWorks";
import EHRFaq from "./components/EHRFaqSection";

// Styled Components
import { StyledMainPageContainer } from "index.styles";
import { StyledSectionContainerColorWrapper } from "styles/components/SectionContainer/index.styles";
import { StyledSectionContainer } from "styles/components/SectionContainer/index.styles";
import { TwoColumnSection } from "styles/components/twoColumnSection";
import { StyledEHRBannerContainer } from "./components/EHRBanner/index.styles";

// Data
import seoData from "./seo.data.json";
import ehrSchemaData from "./schema.organization.json";
import { facilitatorContent, healingCenterContent } from "./data/ehrContent";

// Utils & Hooks
import { constructSchemaData } from "utils/helpers/constructSchemaData";
import useImagePreloader from "utils/hooks/useImagePreloader";
import useFetchEHRProducts from "./hooks/useFetchEHRProducts";
import useMediaQueries from "utils/mediaQueries.utils";

// Animations
import { listVariants } from "styles/animations/staggeredList.animations";
import { listItemShopBannerVariants } from "styles/animations/staggeredList.animations";
import { headerContainerTitleShopBannerVariants } from "styles/animations/headerContainer.animations";

// Header component similar to Ketamine Therapy packages
const EHRHeader = React.memo(() => {
  const { isLg } = useMediaQueries();

  const Component1 = () => {
    return (
      <Grid item xs={12} sm={12} md={10} lg={6} xl={6} order="2">
        <Stack direction="column" spacing={{ xs: 1, sm: 2 }} sx={{ margin: "0rem !important" }}>
          <HeaderContainer
            motionProps={{
              variants: {
                child: headerContainerTitleShopBannerVariants,
              },
              animate: "show",
              initial: "hidden",
            }}
            title="Electronic Health Records with Almadelic"
            textColor={theme.palette.white.main}
            component="h1"
            variant="h1"
            highlightText="Electronic Health Records"
            highlightColor={theme.palette.accent.main}
          />
          <ListComponent
            motionProps={{
              variants: {
                listItem: listItemShopBannerVariants,
                list: listVariants,
                animate: "show",
                initial: "hidden",
              },
            }}
            listVariant={isLg ? "body1" : "body2"}
            icon={
              <CheckCircleOutlineIcon
                role="img"
                aria-label="Checkmark icon"
                aria-hidden="false"
                sx={{ color: theme.palette.accent.main, height: { xs: 18, md: 24, lg: 28 }, width: { xs: 18, md: 24, lg: 28 } }}
              />
            }
            listItems={["Appointment Booking", "Automated Patient Flow", "State Compliant"]}
            color={theme.palette.white.main}
          />
        </Stack>
      </Grid>
    );
  };

  const Component2 = () => {
    return (
      <Grid item xs={12} sm={12} md={10} lg={5} xl={4} order={"1"}>
        {/* Empty grid item for spacing */}
      </Grid>
    );
  };

  return (
    <>
      <StyledEHRBannerContainer id="ehr-packages-header-container">
        <TwoColumnSection
          leftComponent={<Component1 />}
          rightComponent={<Component2 />}
          columnGap={6}
          rowGap={{ xs: 1, sm: 2 }}
          direction="row-reverse"
          id="ehr-header-section"
        />
      </StyledEHRBannerContainer>
    </>
  );
});

const EHRPackagesPage = () => {
  const { isLg } = useMediaQueries();
  const schemaData = constructSchemaData(ehrSchemaData.url, ehrSchemaData.breadcrumbs);
  const { ehrPackages, loading, error } = useFetchEHRProducts();
  const { setIsCartOpen, addItemToCart } = useContext(CartContext);
  const [userType, setUserType] = useState("facilitators");
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Get dynamic content based on user type (for mobile toggle)
  const isHealing = userType === "healingCenters";
  const currentContent = isHealing ? healingCenterContent : facilitatorContent;
  const howItWorksData = currentContent.content.howItWorks;

  // Preload any banner images if needed
  useImagePreloader([]);

  // Get real products instead of mock data
  const facilitatorProducts = ehrPackages.filter((pkg) => pkg.metadata?.userType === "facilitator");
  const healingCenterProducts = ehrPackages.filter((pkg) => pkg.metadata?.userType === "healing center");

  // Set current products based on user type
  const currentProducts = userType === "facilitators" ? facilitatorProducts : healingCenterProducts;

  const handleUserTypeChange = (event, newUserType) => {
    if (newUserType !== null) {
      setUserType(newUserType);
      // Set the first product of the selected type as default
      const newProducts = newUserType === "facilitators" ? facilitatorProducts : healingCenterProducts;
      if (newProducts.length > 0) {
        setSelectedProduct(newProducts[0]);
      } else {
        setSelectedProduct(null);
      }
    }
  };

  // Handle add to cart for CTA button (mobile - uses current toggle state)
  const handleCTAAddToCart = () => {
    const targetProduct = currentProducts.length > 0 ? currentProducts[0] : null;

    if (targetProduct) {
      // Ensure EHR products have the correct structure for cart
      const cartProduct = {
        ...targetProduct,
        // Keep the original metadata.type as "ehr-subscription" for proper validation
        // Ensure we have required display fields
        images: targetProduct.images || ["https://d3a0rs3velfrz.cloudfront.net/logos/almadelicButterFlyTransparent.webp"],
        metadata: {
          ...targetProduct.metadata,
          priceValue: `$${(targetProduct.price / 100).toFixed(2)}/month`,
          product_description:
            targetProduct.description || `EHR ${targetProduct.metadata?.userType || "subscription"} package with comprehensive features.`,
        },
      };
      addItemToCart(cartProduct);
      setIsCartOpen(true);
    }
  };

  // Handle add to cart for specific product type (desktop)
  const handleSpecificAddToCart = (productType) => {
    const products = productType === "facilitator" ? facilitatorProducts : healingCenterProducts;
    const targetProduct = products.length > 0 ? products[0] : null;

    if (targetProduct) {
      // Ensure EHR products have the correct structure for cart
      const cartProduct = {
        ...targetProduct,
        // Keep the original metadata.type as "ehr-subscription" for proper validation
        // Ensure we have required display fields
        images: targetProduct.images || ["https://d3a0rs3velfrz.cloudfront.net/logos/almadelicButterFlyTransparent.webp"],
        metadata: {
          ...targetProduct.metadata,
          priceValue: `$${(targetProduct.price / 100).toFixed(2)}/month`,
          product_description:
            targetProduct.description || `EHR ${targetProduct.metadata?.userType || "subscription"} package with comprehensive features.`,
        },
      };
      addItemToCart(cartProduct);
      setIsCartOpen(true);
    }
  };

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  if (loading) {
    return (
      <StyledSectionContainer cssProps={{ minHeight: "60vh" }}>
        <CBLoading loadingItem={"EHR packages"} />
      </StyledSectionContainer>
    );
  }

  return (
    <StyledMainPageContainer sx={{ position: "relative" }} id="ehr-packages-page">
      <SchemaComponent schemas={schemaData} />
      <SEO title={seoData.title} description={seoData.description} canonicalUrl={seoData.canonicalUrl} />

      {/* Header Section with Hero Image */}
      <EHRHeader />

      {/* Toggle Section - Only show on mobile */}
      <Box
        sx={{
          display: { xs: "flex", lg: "none" },
          justifyContent: "center",
          paddingTop: { xs: "1rem", sm: "2rem" },
          paddingBottom: { xs: "0.5rem", sm: "2rem" },
          paddingX: { xs: "1rem", sm: 0 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            padding: { xs: "3px", sm: "6px" },
            backgroundColor: theme.palette.grey[100],
            borderRadius: "12px",
            boxShadow: "inset 0 1px 3px rgba(0,0,0,0.12)",
            border: `1px solid ${theme.palette.grey[200]}`,
            width: { xs: "100%", sm: "auto" },
            maxWidth: { xs: "100%", sm: "none" },
            minWidth: { xs: "auto", sm: "400px" },
          }}
        >
          {/* Sliding background */}
          <Box
            sx={{
              position: "absolute",
              top: { xs: "3px", sm: "6px" },
              left: userType === "facilitators" ? { xs: "3px", sm: "6px" } : "50%",
              width: "calc(50% - 6px)",
              height: { xs: "calc(100% - 6px)", sm: "calc(100% - 12px)" },
              backgroundColor: theme.palette.primary.main,
              borderRadius: "8px",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              boxShadow: "0 2px 4px rgba(0,0,0,0.15), 0 1px 2px rgba(0,0,0,0.1)",
            }}
          />

          {/* Toggle buttons */}
          <Box
            onClick={() => handleUserTypeChange(null, "facilitators")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleUserTypeChange(null, "facilitators");
              }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={userType === "facilitators"}
            aria-label="Select facilitator EHR packages"
            sx={{
              position: "relative",
              zIndex: 2,
              padding: { xs: "12px 8px", sm: "10px 20px" },
              borderRadius: "8px",
              cursor: "pointer",
              transition: "color 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              color: userType === "facilitators" ? "#ffffff !important" : theme.palette.grey[700],
              fontWeight: "600",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              width: "50%",
              textAlign: "center",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: { xs: "44px", sm: "auto" },
              "&:hover": {
                color: userType === "facilitators" ? "#ffffff !important" : theme.palette.grey[800],
              },
              "&:focus": {
                outline: `2px solid ${theme.palette.accent.main}`,
                outlineOffset: "2px",
              },
            }}
          >
            <MKTypography
              aria-hidden="true"
              sx={{
                color: userType === "facilitators" ? "#ffffff !important" : theme.palette.grey[700],
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                fontWeight: "600",
                textAlign: "center",
                lineHeight: { xs: 1.2, sm: 1.4 },
              }}
            >
              For Facilitators
            </MKTypography>
          </Box>

          <Box
            onClick={() => handleUserTypeChange(null, "healingCenters")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleUserTypeChange(null, "healingCenters");
              }
            }}
            role="button"
            tabIndex={0}
            aria-pressed={userType === "healingCenters"}
            aria-label="Select healing center EHR packages"
            sx={{
              position: "relative",
              zIndex: 2,
              padding: { xs: "12px 8px", sm: "10px 20px" },
              borderRadius: "8px",
              cursor: "pointer",
              transition: "color 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              color: userType === "healingCenters" ? "#ffffff !important" : theme.palette.grey[700],
              fontWeight: "600",
              fontSize: { xs: "0.75rem", sm: "0.85rem" },
              width: "50%",
              textAlign: "center",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: { xs: "44px", sm: "auto" },
              "&:hover": {
                color: userType === "healingCenters" ? "#ffffff !important" : theme.palette.grey[800],
              },
              "&:focus": {
                outline: `2px solid ${theme.palette.accent.main}`,
                outlineOffset: "2px",
              },
            }}
          >
            <MKTypography
              aria-hidden="true"
              sx={{
                color: userType === "healingCenters" ? "#ffffff !important" : theme.palette.grey[700],
                fontSize: { xs: "0.75rem", sm: "0.85rem" },
                fontWeight: "600",
                textAlign: "center",
                lineHeight: { xs: 1.2, sm: 1.4 },
              }}
            >
              For Healing Centers
            </MKTypography>
          </Box>
        </Box>
      </Box>

      {/* Dynamic Content Based on User Type */}
      <Box sx={{ display: { xs: "block", lg: "none" } }} id="mobile-plan-section">
        <DynamicEHRSection
          userType={userType}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          currentProducts={currentProducts}
          shouldUpdateParentState={true}
        />
      </Box>

      {/* Desktop: Show Both Sections */}
      <Box sx={{ display: { xs: "none", lg: "block" } }}>
        {/* Facilitators Section */}
        <Box id="facilitator-plan-section">
          <DynamicEHRSection
            userType="facilitators"
            selectedProduct={facilitatorProducts[0] || null}
            setSelectedProduct={setSelectedProduct}
            currentProducts={facilitatorProducts}
            shouldUpdateParentState={false}
          />
        </Box>

        {/* Divider between sections */}
        <CBDivider marginZero={true} color={theme.palette.accent.main} />

        {/* Healing Centers Section */}
        <Box id="healing-center-plan-section">
          <DynamicEHRSection
            userType="healingCenters"
            selectedProduct={healingCenterProducts[0] || null}
            setSelectedProduct={setSelectedProduct}
            currentProducts={healingCenterProducts}
            shouldUpdateParentState={false}
          />
        </Box>

        {/* Divider after healing center section */}
        <CBDivider marginZero={true} color={theme.palette.accent.main} />
      </Box>

      {/* Features Section - Single instance */}
      <StyledSectionContainer>
        <HeaderContainer
          subtitle="Why Choose Our EHR?"
          title="Built for Modern Healthcare Practices"
          textColor={theme.palette.primary.main}
          highlightColor={theme.palette.secondary.main}
          component="h3"
          variant="h3"
          cssProps={{
            textAlign: "center",
            marginBottom: "3rem !important",
          }}
        />

        <Grid container spacing={4}>
          {[
            {
              icon: (
                <SecurityIcon
                  role="img"
                  aria-label="Security shield icon representing HIPAA compliance"
                  aria-hidden="false"
                  sx={{ fontSize: "3rem", color: theme.palette.accent.main }}
                />
              ),
              title: "HIPAA Compliant",
              description:
                "Enterprise-grade security protocols ensure your patient data remains protected with end-to-end encryption and secure cloud storage.",
            },
            {
              icon: (
                <SpeedIcon
                  role="img"
                  aria-label="Speed dashboard icon representing network access"
                  aria-hidden="false"
                  sx={{ fontSize: "3rem", color: theme.palette.accent.main }}
                />
              ),
              title: "Access To Almadelic Directory",
              description:
                "Become part of a supportive network of mental health professionals dedicated to advancing the field of psychedelic therapy and providing patients with the best possible care.",
            },
            {
              icon: (
                <SupportAgentIcon
                  role="img"
                  aria-label="Support agent headset icon representing 24/7 support"
                  aria-hidden="false"
                  sx={{ fontSize: "3rem", color: theme.palette.accent.main }}
                />
              ),
              title: "24/7 Expert Support",
              description: "Dedicated support team available around the clock to help you maximize your EHR system and ensure smooth operations.",
            },
          ].map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                role="article"
                aria-labelledby={`feature-title-${index}`}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  padding: "2rem",
                  textAlign: "center",
                  border: `1px solid ${theme.palette.grey[300]}`,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 12px 30px rgba(0,0,0,0.15)`,
                    borderColor: theme.palette.secondary.main,
                  },
                }}
              >
                <Box sx={{ marginBottom: "1.5rem" }}>{feature.icon}</Box>
                <MKTypography
                  id={`feature-title-${index}`}
                  variant="h4"
                  component="h4"
                  sx={{ color: theme.palette.primary.main, marginBottom: "1rem", fontWeight: "bold" }}
                >
                  {feature.title}
                </MKTypography>
                <MKTypography variant="body1" sx={{ color: theme.palette.grey[700], lineHeight: 1.6, flexGrow: 1 }}>
                  {feature.description}
                </MKTypography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </StyledSectionContainer>

      {/* How It Works Section */}
      <StyledSectionContainerColorWrapper flipBackground={true}>
        {/* Only render one version based on screen size - no CSS hiding */}
        {isLg === false || (isLg === undefined && typeof window !== "undefined" && window.innerWidth < 1200) ? (
          // Mobile: Show dynamic content based on user type toggle
          <CBHowItWorks content={howItWorksData} />
        ) : (
          // Desktop: Show both sections
          <>
            {/* Facilitators How It Works */}
            <CBHowItWorks content={facilitatorContent.content.howItWorks} />

            {/* Divider between sections */}
            <CBDivider marginZero={true} color={theme.palette.accent.main} />

            {/* Healing Centers How It Works - Reversed layout */}
            <CBHowItWorks content={healingCenterContent.content.howItWorks} reverse={true} />
          </>
        )}

        <CBDivider marginZero={true} color={theme.palette.accent.main} />
        <EHRFaq />
      </StyledSectionContainerColorWrapper>

      {/* Call to Action Section */}
      <StyledSectionContainer>
        <Box
          sx={{
            textAlign: "center",
            maxWidth: "600px",
            margin: "0 auto",
            paddingX: { xs: "1rem", sm: "2rem" },
          }}
        >
          <MKTypography variant="h3" component="h2" sx={{ color: theme.palette.primary.main, marginBottom: "1rem", fontWeight: "bold" }}>
            Start Your EHR Implementation Today
          </MKTypography>
          <MKTypography variant="body1" sx={{ color: theme.palette.grey[600], marginBottom: "2rem", lineHeight: 1.6, fontSize: "1.1rem" }}>
            Join a growing community of mental health professionals who have streamlined their workflow and enhanced patient care with our EHR
            solution.
          </MKTypography>

          {/* Mobile: Single button based on toggle state */}
          <Box sx={{ display: { xs: "flex", lg: "none" }, flexDirection: { xs: "column", sm: "row" }, gap: 2, justifyContent: "center" }}>
            <MKButton
              variant="contained"
              color="secondary"
              size="large"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleCTAAddToCart();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCTAAddToCart();
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Add ${userType === "healingCenters" ? "healing center" : "facilitator"} EHR package to cart - Free prelaunch access`}
              disabled={currentProducts.length === 0}
            >
              {currentProducts.length > 0 ? "Start Free Trial" : "No Products Available"}
            </MKButton>
          </Box>

          {/* Desktop: Two separate buttons */}
          <Box sx={{ display: { xs: "none", lg: "flex" }, flexDirection: "row", gap: 3, justifyContent: "center" }}>
            <MKButton
              variant="contained"
              color="secondary"
              size="large"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSpecificAddToCart("facilitator");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSpecificAddToCart("facilitator");
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Add facilitator EHR package to cart - Free prelaunch access"
              disabled={facilitatorProducts.length === 0}
            >
              {facilitatorProducts.length > 0 ? "Get Facilitator Prelaunch Access" : "Facilitator Package Unavailable"}
            </MKButton>

            <MKButton
              variant="contained"
              color="secondary"
              size="large"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSpecificAddToCart("healing center");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSpecificAddToCart("healing center");
                }
              }}
              role="button"
              tabIndex={0}
              aria-label="Add healing center EHR package to cart - Free prelaunch access"
              disabled={healingCenterProducts.length === 0}
            >
              {healingCenterProducts.length > 0 ? "Get Healing Center Prelaunch Access " : "Healing Center Package Unavailable"}
            </MKButton>
          </Box>
        </Box>
      </StyledSectionContainer>

      <StyledSectionContainerColorWrapper flipBackground={true}>
        <CBDivider marginZero={true} color={theme.palette.accent.main} />
      </StyledSectionContainerColorWrapper>
    </StyledMainPageContainer>
  );
};

export default EHRPackagesPage;
